import React, { useEffect, useState } from "react";
import {
  FolderOpen, FileText, Loader2, Download, Trash2, Search, Clock, CheckCircle2, AlertTriangle, Plus, ListChecks, Send,
} from "lucide-react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "../ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import {
  fetchAllDocuments, reviewDocument, deleteDocument, documentDownloadUrl,
  fetchRequirements, createRequirement, deleteRequirement, listUsers, remindClientDocuments,
  formatDate, formatDateTime,
} from "../../lib/api";

const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

const statusMeta = {
  pending: { label: "Pending", cls: "bg-amber-100 text-amber-700", icon: Clock },
  reviewed: { label: "Reviewed", cls: "bg-green-100 text-green-700", icon: CheckCircle2 },
  attention: { label: "Needs attention", cls: "bg-red-100 text-red-600", icon: AlertTriangle },
};

export default function UserDocuments() {
  const { token } = useAuth();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);
  const [draftStatus, setDraftStatus] = useState("pending");
  const [draftNote, setDraftNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [reqs, setReqs] = useState([]);
  const [newReq, setNewReq] = useState("");
  const [addingReq, setAddingReq] = useState(false);
  const [users, setUsers] = useState([]);
  const [remindingId, setRemindingId] = useState(null);

  const refresh = () => {
    if (!token) return;
    fetchAllDocuments(token).then(setDocs).catch(() => {}).finally(() => setLoading(false));
  };
  const refreshReqs = () => {
    if (!token) return;
    fetchRequirements(token).then(setReqs).catch(() => {});
  };
  useEffect(() => {
    refresh();
    refreshReqs();
    if (token) listUsers(token, "client").then(setUsers).catch(() => {});
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const addReq = async () => {
    const label = newReq.trim();
    if (!label) return;
    setAddingReq(true);
    try {
      await createRequirement(token, label, "");
      setNewReq("");
      refreshReqs();
      toast.success("Requirement added");
    } catch (e) {
      toast.error("Could not add requirement");
    } finally {
      setAddingReq(false);
    }
  };

  const removeReq = async (id) => {
    try {
      await deleteRequirement(token, id);
      refreshReqs();
      toast.success("Requirement removed");
    } catch (e) {
      toast.error("Could not remove requirement");
    }
  };

  const openDoc = (d) => {
    setActive(d);
    setDraftStatus(d.status || "pending");
    setDraftNote(d.admin_note || "");
  };

  const filtered = docs.filter((d) => {
    const q = query.toLowerCase();
    const matchQ = (d.user_name || "").toLowerCase().includes(q) ||
      (d.user_email || "").toLowerCase().includes(q) ||
      (d.original_filename || "").toLowerCase().includes(q);
    const matchF = filter === "All" || d.status === filter;
    return matchQ && matchF;
  });

  const saveReview = async () => {
    setSaving(true);
    try {
      const updated = await reviewDocument(token, active.id, draftStatus, draftNote);
      toast.success("Review saved");
      setDocs((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
      setActive(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save review");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await deleteDocument(token, deleteId);
      setDeleteId(null);
      setActive(null);
      refresh();
      toast.success("Document deleted");
    } catch (e) {
      toast.error("Could not delete document");
    }
  };

  const pendingCount = docs.filter((d) => d.status === "pending").length;

  const sendReminder = async (c) => {
    setRemindingId(c.id);
    try {
      const res = await remindClientDocuments(token, c.id);
      if (res.sent) toast.success(`Reminder sent to ${c.name}`);
      else toast.info(res.message || "Nothing to remind about");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send reminder");
    } finally {
      setRemindingId(null);
    }
  };

  const reqIds = reqs.map((r) => r.id);
  const completion = users
    .map((u) => {
      const userDocs = docs.filter((d) => d.user_id === u.id);
      const provided = new Set(
        userDocs.map((d) => d.requirement_id).filter((id) => id && reqIds.includes(id))
      ).size;
      return { ...u, provided, total: reqIds.length, uploads: userDocs.length };
    })
    .sort((a, b) => a.provided / (a.total || 1) - b.provided / (b.total || 1));

  return (
    <div className="space-y-6" data-testid="user-documents">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-2xl font-bold text-navy flex items-center gap-2">
            <FolderOpen className="h-6 w-6 text-gold-dark" /> User Documents
          </h1>
          <p className="text-gray-500 text-sm mt-0.5">
            {docs.length} document(s){pendingCount > 0 ? ` · ${pendingCount} awaiting review` : ""}
          </p>
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by user or file…"
            className="pl-10 bg-white"
            data-testid="documents-search"
          />
        </div>
      </div>

      {/* Required documents checklist management */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5" data-testid="requirements-card">
        <div className="flex items-center gap-2 mb-3">
          <ListChecks className="h-5 w-5 text-gold-dark" />
          <h2 className="font-serif text-lg font-semibold text-navy">Required Documents Checklist</h2>
        </div>
        <p className="text-sm text-gray-500 mb-4">Define the documents every client should provide. Clients see this list on their portal.</p>
        <div className="flex gap-2">
          <Input
            value={newReq}
            onChange={(e) => setNewReq(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addReq()}
            placeholder="e.g. Photo ID, Pay stub, Bank statement…"
            className="flex-1"
            data-testid="requirement-input"
          />
          <Button onClick={addReq} disabled={addingReq} className="bg-navy hover:bg-navy-light text-white" data-testid="requirement-add-button">
            {addingReq ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1.5" /> Add</>}
          </Button>
        </div>
        {reqs.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {reqs.map((r) => (
              <span key={r.id} className="inline-flex items-center gap-2 bg-cream text-navy text-sm px-3 py-1.5 rounded-lg" data-testid={`requirement-${r.id}`}>
                {r.label}
                <button onClick={() => removeReq(r.id)} className="text-gray-400 hover:text-red-500" data-testid={`requirement-delete-${r.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Per-client completion meter */}
      {reqs.length > 0 && completion.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5" data-testid="completion-card">
          <h2 className="font-serif text-lg font-semibold text-navy mb-4">Client Document Completion</h2>
          <div className="space-y-4">
            {completion.map((c) => {
              const pct = c.total ? Math.round((c.provided / c.total) * 100) : 0;
              const complete = c.provided >= c.total;
              return (
                <div key={c.id} data-testid={`completion-${c.id}`}>
                  <div className="flex items-center justify-between text-sm mb-1.5">
                    <div className="min-w-0">
                      <span className="font-medium text-navy">{c.name}</span>
                      <span className="text-gray-400 text-xs ml-2 hidden sm:inline">{c.email}</span>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className={`text-xs font-semibold ${complete ? "text-green-600" : "text-gold-dark"}`}>
                        {c.provided} of {c.total} received
                      </span>
                      {!complete && (
                        <button
                          onClick={() => sendReminder(c)}
                          disabled={remindingId === c.id}
                          className="inline-flex items-center gap-1 text-xs font-medium text-navy hover:text-gold-dark disabled:opacity-50"
                          data-testid={`reminder-${c.id}`}
                        >
                          {remindingId === c.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                          Remind
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${complete ? "bg-green-500" : "bg-gold"}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        {["All", "pending", "reviewed", "attention"].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-3.5 py-2 rounded-lg text-sm font-medium capitalize transition-colors ${
              filter === s ? "bg-navy text-white" : "bg-white text-gray-600 hover:bg-gray-100"
            }`}
            data-testid={`documents-filter-${s}`}
          >
            {s === "attention" ? "Needs attention" : s}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-navy" /></div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-cream">
                <tr className="text-left text-gray-500">
                  <th className="px-5 py-3.5 font-medium">Document</th>
                  <th className="px-5 py-3.5 font-medium hidden md:table-cell">User</th>
                  <th className="px-5 py-3.5 font-medium hidden sm:table-cell">Uploaded</th>
                  <th className="px-5 py-3.5 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((d) => {
                  const meta = statusMeta[d.status] || statusMeta.pending;
                  const SIcon = meta.icon;
                  return (
                    <tr key={d.id} onClick={() => openDoc(d)} className="border-t border-gray-50 hover:bg-cream/50 cursor-pointer transition-colors" data-testid={`document-row-${d.id}`}>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <FileText className="h-5 w-5 text-gold-dark flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="font-medium text-navy truncate max-w-[220px]">{d.original_filename}</div>
                            <div className="text-xs text-gray-400">{fmtSize(d.size)}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4 hidden md:table-cell">
                        <div className="text-navy">{d.user_name}</div>
                        <div className="text-xs text-gray-400">{d.user_email}</div>
                      </td>
                      <td className="px-5 py-4 text-gray-600 hidden sm:table-cell">{formatDate(d.created_at)}</td>
                      <td className="px-5 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${meta.cls}`}>
                          <SIcon className="h-3.5 w-3.5" /> {meta.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan="4" className="px-5 py-12 text-center text-gray-400">
                      <FolderOpen className="h-10 w-10 mx-auto mb-2 opacity-50" />
                      No documents{query || filter !== "All" ? " match your filters" : " uploaded yet"}.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Review sheet */}
      <Sheet open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="document-review-sheet">
          {active && (
            <>
              <SheetHeader>
                <SheetTitle className="font-serif text-navy">Review Document</SheetTitle>
                <SheetDescription>Download the file, set a status, and add an optional note for the client.</SheetDescription>
              </SheetHeader>
              <div className="mt-4 flex items-center gap-3">
                <div className="h-12 w-12 rounded-lg bg-cream flex items-center justify-center">
                  <FileText className="h-6 w-6 text-gold-dark" />
                </div>
                <div className="min-w-0">
                  <div className="font-medium text-navy break-words">{active.original_filename}</div>
                  <div className="text-xs text-gray-400">{fmtSize(active.size)} · {active.content_type}</div>
                </div>
              </div>

              <div className="mt-4 bg-cream rounded-lg p-3 text-sm">
                <div className="text-navy font-medium">{active.user_name}</div>
                <div className="text-gray-500 text-xs">{active.user_email}</div>
                <div className="text-gray-400 text-xs mt-1">Uploaded {formatDateTime(active.created_at)}</div>
              </div>

              <a
                href={documentDownloadUrl(token, active.id)}
                target="_blank"
                rel="noreferrer"
                className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-navy hover:bg-navy-light text-white font-medium text-sm py-2.5 transition-colors"
                data-testid="document-review-download"
              >
                <Download className="h-4 w-4" /> View / Download
              </a>

              <div className="mt-6">
                <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Status</label>
                <Select value={draftStatus} onValueChange={setDraftStatus}>
                  <SelectTrigger className="mt-1.5" data-testid="document-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="reviewed">Reviewed</SelectItem>
                    <SelectItem value="attention">Needs attention</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="mt-4">
                <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Note to client (optional)</label>
                <Textarea
                  value={draftNote}
                  onChange={(e) => setDraftNote(e.target.value)}
                  placeholder="Add a short note the client will see…"
                  rows={3}
                  className="mt-1.5"
                  data-testid="document-note-input"
                />
              </div>

              <Button onClick={saveReview} disabled={saving} className="mt-4 w-full bg-gold hover:bg-gold-dark text-navy font-semibold" data-testid="document-save-review">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Review"}
              </Button>

              <button
                onClick={() => setDeleteId(active.id)}
                className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-red-500 hover:bg-red-50 text-sm font-medium transition-colors"
                data-testid="document-delete-button"
              >
                <Trash2 className="h-4 w-4" /> Delete Document
              </button>
            </>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this document?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the document from review. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
